import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, FileSpreadsheet, Plus, Trash2 } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';
import ScopeBoundary from '../components/ScopeBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function InventoryBuilder() {
  const [step, setStep] = useState('form'); // form, results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orgData, setOrgData] = useState({
    organization_name: '',
    email: ''
  });
  const [tools, setTools] = useState([{
    name: '',
    primary_use: '',
    who_uses: '',
    data_access: '',
    approved: '',
    notes: ''
  }]);
  const [result, setResult] = useState(null);

  const primaryUses = ['Content', 'Code', 'Customer service', 'Data', 'Productivity', 'Other'];
  const whoUses = ['All staff', 'Specific team', 'Individual', 'External vendors'];
  const dataAccess = ['No sensitive data', 'Internal data only', 'Customer data', 'Financial data', 'Health data'];
  const approvedOptions = ['Yes formally', 'Informally', 'Not yet'];

  const addTool = () => {
    if (tools.length < 10) {
      setTools([...tools, { name: '', primary_use: '', who_uses: '', data_access: '', approved: '', notes: '' }]);
    }
  };

  const removeTool = (index) => {
    if (tools.length > 1) {
      setTools(tools.filter((_, i) => i !== index));
    }
  };

  const updateTool = (index, field, value) => {
    const newTools = [...tools];
    newTools[index][field] = value;
    setTools(newTools);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!orgData.organization_name || !orgData.email) {
      setError('Please fill in organization name and email');
      return;
    }

    const validTools = tools.filter(t => t.name.trim() !== '');
    if (validTools.length === 0) {
      setError('Please add at least one AI tool');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/tools/inventory-builder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...orgData,
          tools: validTools,
          source_tool: 'inventory-builder'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
        setStep('results');
      } else {
        setError(data.detail || 'Failed to generate inventory');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const unapprovedCount = tools.filter(t => t.approved === 'Not yet' && t.name.trim()).length;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/free-tools" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#102A43] mb-8 text-sm">
            <ArrowLeft size={16} />
            Back to Free Tools
          </Link>

          {step === 'form' && (
            <>
              <div className="text-center mb-12">
                <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
                  FREE TOOL
                </p>
                <h1 className="text-[36px] font-bold text-[#102A43] mb-4 leading-tight">
                  Build your AI tool inventory in under three minutes — free
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Document every AI tool your organization uses in a structured, downloadable inventory register.
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Organization Info */}
                <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 mb-6">
                  <h2 className="text-[18px] font-bold text-[#102A43] mb-4">Organization Details</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#102A43] mb-1">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        value={orgData.organization_name}
                        onChange={(e) => setOrgData({ ...orgData, organization_name: e.target.value })}
                        className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                        required
                        data-testid="inventory-org-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#102A43] mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={orgData.email}
                        onChange={(e) => setOrgData({ ...orgData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                        required
                        data-testid="inventory-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Tools List */}
                <div className="space-y-4 mb-6">
                  {tools.map((tool, index) => (
                    <div key={index} className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[16px] font-bold text-[#102A43]">AI Tool {index + 1}</h3>
                        {tools.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTool(index)}
                            className="text-[#94A3B8] hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#102A43] mb-1">Tool Name</label>
                          <input
                            type="text"
                            value={tool.name}
                            onChange={(e) => updateTool(index, 'name', e.target.value)}
                            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                            placeholder="e.g., ChatGPT, Copilot"
                            data-testid={`inventory-tool-name-${index}`}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#102A43] mb-1">Primary Use</label>
                          <select
                            value={tool.primary_use}
                            onChange={(e) => updateTool(index, 'primary_use', e.target.value)}
                            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                          >
                            <option value="">Select...</option>
                            {primaryUses.map(use => <option key={use} value={use}>{use}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#102A43] mb-1">Who Uses It</label>
                          <select
                            value={tool.who_uses}
                            onChange={(e) => updateTool(index, 'who_uses', e.target.value)}
                            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                          >
                            <option value="">Select...</option>
                            {whoUses.map(w => <option key={w} value={w}>{w}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#102A43] mb-1">Data Access Level</label>
                          <select
                            value={tool.data_access}
                            onChange={(e) => updateTool(index, 'data_access', e.target.value)}
                            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                          >
                            <option value="">Select...</option>
                            {dataAccess.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#102A43] mb-1">Approved?</label>
                          <select
                            value={tool.approved}
                            onChange={(e) => updateTool(index, 'approved', e.target.value)}
                            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                          >
                            <option value="">Select...</option>
                            {approvedOptions.map(a => <option key={a} value={a}>{a}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#102A43] mb-1">Notes (optional)</label>
                          <input
                            type="text"
                            value={tool.notes}
                            onChange={(e) => updateTool(index, 'notes', e.target.value)}
                            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {tools.length < 10 && (
                  <button
                    type="button"
                    onClick={addTool}
                    className="w-full border-2 border-dashed border-[#E2E8F0] rounded-[10px] py-4 text-[#64748B] hover:border-[#2F80ED] hover:text-[#2F80ED] transition-colors flex items-center justify-center gap-2 mb-6"
                  >
                    <Plus size={18} />
                    Add Another Tool ({tools.length}/10)
                  </button>
                )}

                {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2F80ED] text-white font-semibold py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="inventory-submit-btn"
                >
                  {loading ? 'Generating...' : 'Generate My Inventory'}
                  <ArrowRight size={18} />
                </button>
              </form>
            </>
          )}

          {step === 'results' && result && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileSpreadsheet className="text-[#15803D]" size={32} />
                </div>
                <h1 className="text-[32px] font-bold text-[#102A43] mb-2">
                  Your AI Tool Inventory is ready
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  {result.tool_count} tools documented for {orgData.organization_name}
                </p>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-[18px] font-bold text-[#102A43]">
                      AI Tool Inventory
                    </h2>
                    <p className="text-[14px] text-[#64748B]">
                      PDF document · Sent to {orgData.email}
                    </p>
                  </div>
                  {result.download_url && (
                    <a
                      href={result.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-5 py-2 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                  )}
                </div>

                {result.unapproved_count > 0 && (
                  <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-[10px] p-4 mb-6">
                    <p className="text-[14px] text-[#B91C1C] font-medium">
                      ⚠️ {result.unapproved_count} tool{result.unapproved_count > 1 ? 's' : ''} marked as "Not yet approved"
                    </p>
                    <p className="text-[13px] text-[#B91C1C] mt-1">
                      Consider establishing a formal AI tool approval process.
                    </p>
                  </div>
                )}

                <ScopeBoundary
                  delivers={[
                    'A structured register of up to 10 AI tools in use',
                    'Unapproved tools flagged and highlighted'
                  ]}
                  doesNotDeliver={[
                    'A policy governing those tools',
                    'A risk register assessing each tool\'s risk level',
                    'Vendor assessment scoring for the vendors behind those tools',
                    'An incident log for when those tools cause issues'
                  ]}
                />

                <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-[10px] p-6">
                  <p className="text-[15px] text-[#102A43] mb-4">
                    Your AI Tool Inventory documents what you have. The AI Accountability Documentation System gives you the policy, risk register, vendor assessments, and incident log that turn that inventory into a governed, auditable framework. $97 one-time.
                  </p>
                  <Link
                    to="/documentation-system"
                    className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                  >
                    Get the Documentation System — $97
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default InventoryBuilder;
